import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';
import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExpenseTable = ({ expenses, onEdit, onDelete, category }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'annually': 'Annually',
      'one-time': 'One-time'
    };
    return labels[frequency] || frequency;
  };

  const calculateAnnualAmount = (amount, frequency) => {
    const multipliers = {
      'monthly': 12,
      'quarterly': 4,
      'annually': 1,
      'one-time': 1
    };
    return amount * (multipliers[frequency] || 1);
  };

  const categoryExpenses = category === 'all' ? expenses : expenses.filter(expense => expense.category === category);

  if (categoryExpenses.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No expenses added</h3>
        <p className="text-muted-foreground mb-4">Start by adding your first expense in this category</p>
      </div>
    );
  }

  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 font-medium text-foreground">Description</th>
            <th className="text-left py-3 px-4 font-medium text-foreground">Amount</th>
            <th className="text-left py-3 px-4 font-medium text-foreground">Frequency</th>
            <th className="text-left py-3 px-4 font-medium text-foreground">Duration</th>
            <th className="text-left py-3 px-4 font-medium text-foreground">Inflation</th>
            <th className="text-left py-3 px-4 font-medium text-foreground">Probability</th>
            <th className="text-left py-3 px-4 font-medium text-foreground">Annual Cost</th>
            <th className="text-right py-3 px-4 font-medium text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {categoryExpenses.map((expense) => (
            <tr key={expense.id} className="border-b border-border hover:bg-muted/50 transition-colors">
              <td className="py-3 px-4">
                <div className="font-medium text-foreground">{expense.description}</div>
                {expense.isOneTime && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning mt-1">
                    One-time
                  </span>
                )}
              </td>
              <td className="py-3 px-4 text-foreground">
                {formatCurrency(expense.amount)}
              </td>
              <td className="py-3 px-4 text-muted-foreground">
                {getFrequencyLabel(expense.frequency)}
              </td>
              <td className="py-3 px-4 text-muted-foreground">
                {expense.startYear} - {expense.endYear}
              </td>
              <td className="py-3 px-4 text-muted-foreground">
                {expense.inflationRate}%
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <div className="w-12 bg-muted rounded-full h-2">
                    <div 
                      className="h-2 bg-accent rounded-full" 
                      style={{ width: `${expense.probability}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground">{expense.probability}%</span>
                </div>
              </td>
              <td className="py-3 px-4 font-medium text-foreground">
                {formatCurrency(calculateAnnualAmount(expense.amount, expense.frequency))}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(expense)}
                    className="h-8 w-8"
                    disabled={!effectiveCanWrite}
                    title={isDemoModeActive ? 'Cannot edit in demo mode' : 'Edit'}
                  >
                    <Icon name="Edit2" size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(expense.id)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    disabled={!effectiveCanWrite}
                    title={isDemoModeActive ? 'Cannot delete in demo mode' : 'Delete'}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExpenseTable;